import { formatCurrency } from '@if/core/utils'
import ErrorIcon from '@mui/icons-material/Error'
import { Alert, Grid, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import type { FieldArrayPathByValue } from '@src/services/react-hook-form'
import { memo, useState } from 'react'
import { FieldErrors, useController, useFieldArray, type Control, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddButton from '../Common/Button/AddButton'
import DeleteIconButton from '../Common/Button/DeleteIconButton'
import EditIconButton from '../Common/Button/EditIconButton'
import ActionTableCell from '../Common/Table/ActionTableCell'
import type { DisbursementLineSchemaType } from './loan-form-schema'
import DisbursementLineDialog from './DisbursementLineDialog'

interface DisbursementLineRowProps {
  disbursementLine: DisbursementLineSchemaType
  errors?: FieldErrors<DisbursementLineSchemaType>
  onDelete: () => void
  onUpdate: (data: DisbursementLineSchemaType) => void
}

const DisbursementLineRow = memo(({ disbursementLine, errors, onUpdate, onDelete }: DisbursementLineRowProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <TableRow>
      <TableCell>
        {t(`enum.product.${disbursementLine.productId}`)}
        {errors?.productId?.message && (
          <Typography variant="inherit" color="error">
            {errors?.productId?.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {disbursementLine.amount && formatCurrency(disbursementLine.amount)}
        {errors?.amount?.message && (
          <Typography variant="inherit" color="error">
            {errors?.amount?.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {disbursementLine.description}
        {errors?.description?.message && (
          <Typography variant="inherit" color="error">
            {errors?.description?.message}
          </Typography>
        )}
      </TableCell>
      <ActionTableCell>
        <EditIconButton onClick={() => setOpen(true)} />
        <DeleteIconButton onClick={onDelete} />
      </ActionTableCell>
      <DisbursementLineDialog open={open} defaultValues={disbursementLine} onClose={() => setOpen(false)} onSubmit={onUpdate} />
    </TableRow>
  )
})

DisbursementLineRow.displayName = 'DisbursementLineRow'

interface Props<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldArrayPathByValue<TFieldValues, DisbursementLineSchemaType>
  onInsert: (data: DisbursementLineSchemaType) => void
  onUpdate: (data: DisbursementLineSchemaType, index: number) => void
  onDelete: (index: number) => void
}

const DisbursementLineList = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  onInsert,
  onUpdate,
  onDelete,
}: Props<TFieldValues>) => {
  const { fields, remove, update, append } = useFieldArray({ name, control })
  const {
    fieldState: { error },
  } = useController({ name: name as never, control })
  const [open, setOpen] = useState(false)

  const onSubmit = (data: DisbursementLineSchemaType) => {
    append(data as never)
    onInsert(data)
  }

  const onLineUpdate = (data: DisbursementLineSchemaType, index: number) => {
    update(index, data as never)
    onUpdate(data, index)
  }

  const onLineRemoved = (index: number) => {
    remove(index)
    onDelete(index)
  }

  return (
    <Grid size={12}>
      {error?.message && (
        <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
          {error?.message}
        </Alert>
      )}
      <Table>
        <TableBody>
          {fields.map((fundedProduct, index) => {
            const { id, productId, amount, description } = fundedProduct as unknown as DisbursementLineSchemaType & {
              id: string
            }
            return (
              <DisbursementLineRow
                key={id}
                errors={error ? (error as never)[index] : undefined}
                disbursementLine={{ productId, amount, description }}
                onDelete={() => onLineRemoved(index)}
                onUpdate={(data: DisbursementLineSchemaType) => onLineUpdate(data, index)}
              />
            )
          })}
        </TableBody>
      </Table>
      <Stack direction="row" alignItems="center" sx={{ justifyContent: 'flex-end' }} margin={1}>
        <AddButton variant="contained" onClick={() => setOpen(true)} />
      </Stack>
      <DisbursementLineDialog open={open} onClose={() => setOpen(false)} onSubmit={onSubmit} />
    </Grid>
  )
}

export default DisbursementLineList
